import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Layout, Input, Select, Card, Button, Pagination, Empty, Spin, Modal, Space, message } from 'antd';
import { SearchOutlined, EditOutlined, DownloadOutlined, LeftOutlined, RightOutlined, PictureOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { posters, categories, proxyImageUrl, downloadImageFile } from '../../api';
import './Home.css';

const { Header, Content } = Layout;
const MOBILE_MEDIA_QUERY = '(max-width: 640px)';
const HOME_DESKTOP_THUMBNAIL_OPTIONS = { width: 540, height: 960, mode: 'fill', quality: 82 };
const HOME_MOBILE_THUMBNAIL_OPTIONS = { width: 360, height: 640, mode: 'fill', quality: 80 };

function getIsMobile() {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA_QUERY).matches;
}

function Home() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 12, total: 0 });
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewPosterUuid, setPreviewPosterUuid] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(-1);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [searchValue, setSearchValue] = useState(searchParams.get('keyword') || '');
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef(null);

  const category = searchParams.get('category') || undefined;
  const keyword = searchParams.get('keyword') || '';
  const thumbnailOptions = isMobile ? HOME_MOBILE_THUMBNAIL_OPTIONS : HOME_DESKTOP_THUMBNAIL_OPTIONS;

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handleChange = () => setIsMobile(mediaQuery.matches);

    handleChange();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    setData([]);
    setHasMore(true);
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [isMobile]);

  useEffect(() => {
    setSearchValue(keyword);
  }, [keyword]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchData({ append: isMobile && pagination.current > 1 });
  }, [category, keyword, pagination.current, pagination.pageSize, isMobile]);

  useEffect(() => {
    if (!isMobile || !loadMoreRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loading) {
          setPagination((prev) => ({ ...prev, current: prev.current + 1 }));
        }
      },
      { rootMargin: '240px 0px' }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [isMobile, hasMore, loading]);

  const activeCategoryLabel = useMemo(() => {
    if (!category) {
      return '全部分类';
    }

    return categoryList.find((item) => String(item.id) === String(category))?.name || '全部分类';
  }, [category, categoryList]);

  const updateQueryParams = (nextValues = {}) => {
    const nextParams = new URLSearchParams(searchParams);

    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        nextParams.delete(key);
      } else {
        nextParams.set(key, value);
      }
    });

    setSearchParams(nextParams);
    setData([]);
    setHasMore(true);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const fetchData = async ({ append = false } = {}) => {
    setLoading(true);

    try {
      let response;
      const requestCategory = isMobile ? undefined : category;

      if (keyword) {
        response = await posters.search({
          keyword,
          category_id: requestCategory,
          page: pagination.current,
          pageSize: pagination.pageSize
        });
      } else {
        const params = {
          page: pagination.current,
          pageSize: pagination.pageSize
        };

        if (requestCategory) {
          params.category_id = requestCategory;
        }

        response = await posters.list(params);
      }

      const nextData = response.data.data || [];
      const nextTotal = response.data.pagination?.total || 0;

      setData((prev) => (append ? [...prev, ...nextData] : nextData));
      setPagination((prev) => ({
        ...prev,
        total: nextTotal
      }));
      setHasMore(pagination.current * pagination.pageSize < nextTotal);
    } catch (err) {
      setData([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categories.list();
      setCategoryList(response.data.data || []);
    } catch (err) {
      setCategoryList([]);
    }
  };

  const handleSearch = (value) => {
    updateQueryParams({ keyword: value?.trim() || undefined });
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    handleSearch(searchValue);
  };

  const handleCategoryChange = (value) => {
    updateQueryParams({ category: value || undefined });
  };

  const handleDownload = async (url, title) => {
    try {
      await downloadImageFile(url, title);
    } catch (err) {
      message.error(err.message || '下载失败');
    }
  };

  const openImageEditor = (item) => {
    const posterUuid = item.uuid || item.id;
    window.open(`https://imgedit.zl6.cn/?posterUuid=${encodeURIComponent(posterUuid)}`, '_blank', 'noopener,noreferrer');
  };

  const openPreviewByIndex = (index) => {
    const nextItem = data[index];

    if (!nextItem) {
      return;
    }

    setPreviewLoading(true);
    setPreviewImage(nextItem.image_url);
    setPreviewTitle(nextItem.title);
    setPreviewPosterUuid(nextItem.uuid || String(nextItem.id));
    setPreviewIndex(index);
    setPreviewVisible(true);
  };

  const handlePreview = (item) => {
    setPreviewLoading(true);
    setPreviewImage(item.image_url);
    setPreviewTitle(item.title);
    setPreviewPosterUuid(item.uuid || String(item.id));
    setPreviewIndex(data.findIndex((dataItem) => dataItem.id === item.id));
    setPreviewVisible(true);
  };

  const handlePreviewStep = (direction) => {
    const nextIndex = previewIndex + direction;

    if (nextIndex < 0 || nextIndex >= data.length) {
      return;
    }

    openPreviewByIndex(nextIndex);
  };

  return (
    <Layout className="home-layout">
      <Header className="home-header">
        <div className="home-brand">
          <h1 className="home-brand-title">瑞华海报仓库</h1>
          <div className="home-brand-subtitle">集中管理、快速下载、在线编辑常用海报素材。</div>
        </div>

        <div className="home-header-search">
          <Button
            icon={<PictureOutlined />}
            className="home-library-btn"
            onClick={() => navigate('/image-library')}
          >
            图片库
          </Button>
          <Select
            className="home-category-select"
            placeholder="全部分类"
            allowClear
            value={category}
            onChange={handleCategoryChange}
            options={categoryList.map((item) => ({
              label: item.name,
              value: String(item.id)
            }))}
          />
          <form className="home-search-box" onSubmit={handleSearchSubmit}>
            <Input
              placeholder="搜索标题或简介"
              allowClear
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              className="home-search-input"
            />
            <Button
              icon={<SearchOutlined />}
              className="home-search-btn"
              htmlType="submit"
            >
              搜索
            </Button>
          </form>
        </div>
      </Header>

      <Content className="home-content">
        <div className="home-toolbar">
          <div className="home-toolbar-left">
            <div className="home-toolbar-label">当前分类</div>
            <div className="home-toolbar-value">{activeCategoryLabel}</div>
          </div>
          <div className="home-toolbar-right">
            共 <strong>{pagination.total}</strong> 张海报
          </div>
        </div>

        <Spin spinning={loading}>
          {data.length > 0 ? (
            <>
              <div className="home-grid">
                {data.map((item) => (
                  <Card
                    key={item.id}
                    hoverable
                    className="home-card"
                    bodyStyle={{ padding: 16 }}
                    cover={
                      <div
                        className="home-card-cover"
                        onClick={() => handlePreview(item)}
                      >
                        <img
                          alt={item.title}
                          src={proxyImageUrl(item.image_url, thumbnailOptions)}
                          className="home-card-image"
                        />
                      </div>
                    }
                  >
                    <div className="home-card-meta">
                      <div className="home-card-title">{item.title}</div>
                      <div className="home-card-desc">{item.description || item.category_name || '暂无简介'}</div>
                    </div>

                    <div className="home-card-actions">
                      {!isMobile ? (
                        <Button
                          icon={<EditOutlined />}
                          className="home-card-action-btn"
                          onClick={() => openImageEditor(item)}
                        >
                          编辑
                        </Button>
                      ) : null}
                      <Button
                        icon={<DownloadOutlined />}
                        className="home-card-action-btn"
                        onClick={() => handleDownload(item.image_url, item.title)}
                      >
                        下载
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>

              {isMobile ? (
                <div className="home-mobile-load" ref={loadMoreRef}>
                  {loading ? '加载中...' : hasMore ? '继续下滑查看更多' : '已经到底了'}
                </div>
              ) : (
                <div className="home-pagination-wrap">
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    showSizeChanger
                    showQuickJumper
                    pageSizeOptions={['12', '24', '36', '48']}
                    showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
                    onChange={(page, pageSize) => {
                      setPagination((prev) => ({
                        ...prev,
                        current: page,
                        pageSize
                      }));
                    }}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="home-empty-wrap">
              <Empty description="暂无海报内容" />
              <Space>
                {!isMobile ? <Button onClick={() => handleCategoryChange(undefined)}>清空分类</Button> : null}
                <Button onClick={() => handleSearch('')}>清空搜索</Button>
              </Space>
            </div>
          )}
        </Spin>

        <Modal
          open={previewVisible}
          footer={null}
          onCancel={() => setPreviewVisible(false)}
          width="auto"
          centered
          closable={false}
          className="home-preview-modal"
          maskStyle={{ background: 'rgba(15, 23, 42, 0.72)', backdropFilter: 'blur(8px)' }}
        >
          <div className="home-preview">
            <div className="home-preview-stage">
              {!isMobile ? (
                <Button
                  icon={<LeftOutlined />}
                  className="home-preview-nav home-preview-nav-left"
                  onClick={() => handlePreviewStep(-1)}
                  disabled={previewIndex <= 0}
                />
              ) : null}
              <div className={`home-preview-image-wrap${previewLoading ? ' home-preview-image-wrap-loading' : ''}`}>
                {previewLoading ? <Spin className="home-preview-loading" /> : null}
                <img
                  src={proxyImageUrl(previewImage)}
                  alt={previewTitle}
                  className={`home-preview-image${previewLoading ? ' home-preview-image-loading' : ''}`}
                  onLoad={() => setPreviewLoading(false)}
                  onError={() => setPreviewLoading(false)}
                />
              </div>
              {!isMobile ? (
                <Button
                  icon={<RightOutlined />}
                  className="home-preview-nav home-preview-nav-right"
                  onClick={() => handlePreviewStep(1)}
                  disabled={previewIndex >= data.length - 1}
                />
              ) : null}
            </div>
            <div className="home-preview-actions">
              {!isMobile ? (
                <Button
                  icon={<EditOutlined />}
                  className="home-preview-action-btn"
                  onClick={() => {
                    setPreviewVisible(false);
                    navigate(`/editor/${previewPosterUuid}`);
                  }}
                >
                  编辑海报
                </Button>
              ) : null}
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                className="home-preview-action-btn"
                onClick={() => handleDownload(previewImage, previewTitle)}
              >
                下载原图
              </Button>
            </div>
          </div>
        </Modal>
      </Content>
    </Layout>
  );
}

export default Home;
